"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClientSideExport = useClientSideExport;
exports.exportData = exportData;
exports.escapeCsv = escapeCsv;
const DialogHelper_1 = require("@juvare/common-web-ui/DialogHelper");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const date_fns_1 = require("date-fns");
/**
 * Hook for client side export. This hook handles constructing and downloading csv files.
 * It also fetches data in chunks.
 * @param config
 */
function useClientSideExport(config) {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const handleExport = async () => {
        (0, DialogHelper_1.showProgressDialog)({
            title: config.title ?? t('common.exporting'),
            callback: async (done) => {
                const localDate = (0, date_fns_1.formatISO9075)(new Date(), {
                    format: 'extended',
                    representation: 'complete',
                }).replace(' ', '-');
                try {
                    const exportedData = await exportData(config);
                    (0, DialogHelper_1.showConfirmDialog)({
                        title: t('common.exportCompleted'),
                        content: t('common.exportCompletedContent'),
                        confirmLabel: t('common.download'),
                        onConfirm: () => {
                            downloadCsvFile(exportedData, `${config.fileName ?? 'export'}-${localDate}.csv`);
                        },
                    });
                }
                catch (e) {
                    (0, DialogHelper_1.handleErrorDialog)(e);
                }
                finally {
                    done();
                }
            },
        });
    };
    return {
        handleExport,
    };
}
async function exportData(config) {
    let exportData = config.header.map(escapeCsv).join(',');
    let offset = 0;
    const limit = config.batchSize ?? 100;
    while (true) {
        const { rows, total } = await config.onFetchData({ offset, limit });
        for (const row of rows) {
            exportData += '\r\n';
            const convertedData = config.onConvertData(row);
            if (convertedData.length !== config.header.length) {
                throw new Error('Exported row count does not match header count');
            }
            exportData += convertedData.map(escapeCsv).join(',');
        }
        if (rows.length === 0 || rows.length + offset >= total) {
            break;
        }
        offset += limit;
    }
    return exportData;
}
function escapeCsv(value) {
    const escaped = (value ?? '').replace(/"/g, '""');
    return /"|,|\r\n|\n|\r/.test(escaped) ? `"${escaped}"` : escaped;
}
function downloadCsvFile(content, fileName) {
    const blob = new Blob([content], { type: 'text/csv' });
    const downloadLink = document.createElement('a');
    const objectURL = URL.createObjectURL(blob);
    downloadLink.href = objectURL;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(objectURL);
}
